import { useLazyQuery, useQuery } from "@apollo/client/react";
import SearchIcon from '@mui/icons-material/Search';
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import 'moment/locale/es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { adaptDelegacionReclamosArray } from '../../../adapters/delegacionAdapter';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { GET_DELEGACIONES } from '../../../data/graphQL/queries/Planes/getDelegaciones';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import ClearIcon from '@mui/icons-material/Clear';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import DownloadIcon from '@mui/icons-material/Download';
import toast from 'react-hot-toast';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { DateInput } from '../../../components/dateInput/DateInput';
import moment from 'moment';
import { CustomChip } from '../../../components/chip/Chip';
import { useDebounce } from '../../../hooks/useDebounce';
import adaptReclamosArray, { GET_RECLAMOS } from '../../../data/graphQL/queries/Reclamos/getReclamos';
import { GET_ALL_AREAS_RECLAMO } from '../../../data/graphQL/queries/Reclamos/getAllAreasReclamo';
import { GET_ALL_ESTADOS_RECLAMO } from '../../../data/graphQL/queries/Reclamos/getAllEstadosReclamo';
import { GET_ALL_CLASIFICACIONES_IR } from '../../../data/graphQL/queries/Reclamos/getAllClasificacionesIR';
import InformeReclamoTemplate from './InformeReclamoTemplate';
import fileDownload from 'js-file-download';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import GET_INFORME_RECLAMO, { adaptInformeReclamo } from '../../../data/graphQL/queries/Reclamos/getInformeReclamo';
import { GET_PRINT_INFORME_RECLAMO } from '../../../data/graphQL/queries/Reclamos/getPrintInformeReclamo';
import HistorialModal from '../../ReclamoCreation/components/tabs/components/HistorialModal';
import EditarResponsablesModal from './EditarResponsablesModal';
import { GET_ROLES_BY_USER } from '../../../data/graphQL/queries/Reclamos/getRolesByUserId';

const storageId = sessionStorage.getItem('idUsuario');

const tipoReclamo = Object.freeze({
  RECLAMO: 1,
  EFI: 2
});

const ROL_JEFE_CALIDAD_ID = 27;
const ROL_RESPONSABLE_CALIDAD_ASISTENCIAL_ID = 30;


const ESTADO_PE = 'Pendiente';
const ESTADO_AP = 'Aprobado';
const ESTADO_RE = 'Rechazado';
const ESTADO_DE = 'Desestimado';
const ESTADO_CE = 'Cerrado';

const vencimientoOptions = [
  { id: 1, displayName: 'Todos' },
  { id: 2, displayName: 'Proximos a vencer' },
  { id: 3, displayName: 'Vencidos' }];


const chipConfig = {
  [ESTADO_PE]: { isYellow: true, title: ESTADO_PE },
  [ESTADO_AP]: { isGreen: true, title: ESTADO_AP },
  [ESTADO_RE]: { isViolet: true, title: ESTADO_RE },
  [ESTADO_CE]: { isGreen: true, title: ESTADO_CE },
  [ESTADO_DE]: { isRed: true, title: ESTADO_DE }
};

const getChipEstado = (estado) => {
  const config = chipConfig[estado];
  if (config) {
    return <CustomChip
      label={config.title}
      isYellow={config.isYellow}
      isViolet={config.isViolet}
      isGreen={config.isGreen}
      isGray={config.isGray}
      isRed={config.isRed}
      title={config.title} />;

  }
  return null;
};

const opcionTodos = { id: -1, displayName: 'Todos' };

const idUsuarioLoggeado = parseInt(sessionStorage.getItem('idUsuario'));

export const ReclamosContent = ({ setChildrenAction }) => {
  const { tipoGestion } = useParams();
  const isEFI = useMemo(() => tipoGestion === 'efi', [tipoGestion]);
  const location = useLocation();
  const navigate = useNavigate();
  const rutasStorage = localStorage.getItem('navigationRoutes');

  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [delegaciones, setDelegaciones] = useState([]);
  const [areasReclamo, setAreasReclamo] = useState([opcionTodos]);
  const [estadosReclamo, setEstadosReclamo] = useState([opcionTodos]);
  const [clasificacionesIR, setClasificacionesIR] = useState([opcionTodos]);
  const [rolesByUserId, setRolesByUserId] = useState([]);

  const [allowedWrite, setAllowedWrite] = useState(false);

  const {
    reclamos,
    reclamosLoading,
    totalCount,
    page,
    pageSize,
    filters,
    handleChangeFilters,
    setReclamos,
    onPageChange,
    onPageSizeChange
  } = useGetReclamos(tipoGestion, delegacionOrigen, isEFI);

  const { data: rolesData } = useQuery(GET_ROLES_BY_USER, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!rolesData) return;

    setRolesByUserId(rolesData?.rolesByUserId?.map((x) => x.id));
  }, [rolesData]);

  const { data: areasData } = useQuery(GET_ALL_AREAS_RECLAMO);

  useEffect(() => {
    if (!areasData) return;

    const areas = areasData?.allAreasReclamo?.map((area) => ({ id: area.idAreaReclamo, displayName: area.descripcion }));
    setAreasReclamo(areas);
  }, [areasData]);

  const { data: estadosData } = useQuery(GET_ALL_ESTADOS_RECLAMO);

  useEffect(() => {
    if (!estadosData) return;

    const estados = estadosData?.allEstadosReclamo?.map((estado) => ({ id: estado.idEstadoReclamo, displayName: estado.descripcion }));
    setEstadosReclamo([opcionTodos, ...estados]);
  }, [estadosData]);

  const { data: clasificacionesData } = useQuery(GET_ALL_CLASIFICACIONES_IR);

  useEffect(() => {
    if (!clasificacionesData) return;

    const clasificaciones = clasificacionesData?.allClasificacionReclamo?.map((clasif) => ({ id: clasif.idClasificacionReclamo, displayName: clasif.descripcion }));
    setClasificacionesIR([opcionTodos, ...clasificaciones]);
  }, [clasificacionesData]);

  const [getInformeReclamo] = useLazyQuery(GET_INFORME_RECLAMO, {
    fetchPolicy: 'no-cache'
  });

  const [getPrintInforme, { data: printInformeData, error: printInformeError }] = useLazyQuery(GET_PRINT_INFORME_RECLAMO, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!printInformeData) return;
    if (printInformeData.printInformeReclamo != null) {
      const arr = new Uint8Array(printInformeData.printInformeReclamo);
      const documento = new Blob([arr], { type: 'application/pdf' });
      const fileName = `Informe_reclamo.pdf`;
      fileDownload(documento, fileName);
    } else {
      throw new Error('error');
      // toast.error('No se pudo exportar el Resumen, intentelo nuevamente más tarde.');
    }
  }, [printInformeData]);

  useEffect(() => {
    if (printInformeError) {
      throw new Error('error');
    }
  }, [printInformeError]);

  const getFechaRegistro = useCallback((fechaRegistro, idEstado) => {
    const now = moment();
    const registroMoment = moment(fechaRegistro);

    const daysDiff = now.diff(registroMoment, 'days');

    let tooltipText = registroMoment.format('DD/MM/YYYY');
    let sx = {
      width: 'fit-content',
      height: '21px',
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: 4
    };

    if (idEstado !== 4 && idEstado !== 5) {
      if (daysDiff > 30) {
        // Vencido (más de 30 días)
        const diasVencidos = daysDiff - 30;
        sx = {
          ...sx,
          backgroundColor: '#E71B3E',
          color: 'white'
        };
        tooltipText = `${diasVencidos} ${diasVencidos > 1 ? 'días' : 'día'} vencido`;
      } else if (daysDiff >= 20 && daysDiff <= 29) {
        // Entre 20 y 29 días
        const diasRestantes = 30 - daysDiff;
        sx = {
          ...sx,
          backgroundColor: '#FBD732',
          color: 'black'
        };
        tooltipText = `${diasRestantes} ${diasRestantes > 1 ? 'días' : 'día'} para vencer`;
      }
    }

    return (
      <Tooltip title={tooltipText}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Box {...sx}>
            {registroMoment.format('DD/MM/YYYY')}
          </Box>
        </Box>
      </Tooltip>);

  }, []);

  const initialColumns = [
    {
      headerName: '',
      sortable: false,
      field: 'action',
      minWidth: 40,
      maxWidth: 40,
      width: 40,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        const allowedExport = row.estado === ESTADO_CE && !isEFI;

        const esUsuarioResponsable = parseInt(idUsuarioLoggeado) === row?.idUsuarioResponsable;

        return (
          <PopoverModal>
            <div className="popover-modal-content">
              <div onClick={() => handleClickView(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <VisibilityIcon className='edit-icon' />
                <span className='toggle-action'>Ver</span>
              </div>
              {allowedWrite &&
                row.estado !== 'Cerrado' &&
                row.estado !== 'Desestimado' &&
                row.puedeEditar &&
                <div onClick={() => handleClickEdit(row)} aria-hidden='true'
                  className='item-option clickable-option'>

                  <EditIcon className='edit-icon' />
                  <span className='toggle-action'>Editar</span>
                </div>
              }
              <HistorialModal
                isPopoverMenuOption
                idGestionReclamo={row.idGestionReclamo}
                esEFI={isEFI}
                estados={estadosReclamo}
                esUsuarioResponsable={esUsuarioResponsable} />

              {allowedExport && <div
                onClick={() => toast.promise(handleExportar(row.idGestionReclamo), {
                  loading: 'Descargando informe...',
                  success: <label>Informe descargado</label>,
                  error: <label>No se pudo descargar el informe.</label>
                })}
                aria-hidden='true'
                className='item-option clickable-option'>
                <DownloadIcon className='edit-icon' />
                <span className='toggle-action'>Descargar</span>
              </div>}
            </div>
          </PopoverModal>);

      }
    },
    {
      width: 60,
      field: 'id',
      headerName: '#'
    },
    {
      width: 140,
      field: 'idDelegacion',
      headerName: 'Delegación',
      valueGetter: (idDelegacion) => {
        return delegaciones.find((delegacion) => delegacion.id === idDelegacion)?.displayName || '-';
      }
    },
    {
      width: 200,
      field: 'idArea',
      headerName: 'Área responsable',
      valueGetter: (idArea) => {
        return areasReclamo.find((area) => area.id === idArea)?.displayName || '-';
      }
    },
    {
      width: 80,
      field: 'estado',
      headerName: 'Estado',
      headerAlign: 'center',
      align: 'center',
      renderCell: (estado) => {
        return getChipEstado(estado.row.estado);
      }
    },
    {
      width: 200,
      field: 'etapa',
      headerName: 'Etapa',
      valueGetter: (_, row) => {
        if (row.estado === ESTADO_CE || row.estado === ESTADO_DE) return '-';
        return row.etapa;
      }
    },
    {
      width: 120,
      field: 'idClasificacionReclamo',
      headerName: 'Clasificación IR',
      valueGetter: (idClasificacionReclamo) => {
        return clasificacionesIR.find((clasif) => clasif.id === idClasificacionReclamo)?.displayName || '-';
      }
    },
    {
      width: 150,
      field: 'fechaRegistro',
      align: 'center',
      headerAlign: 'center',
      headerName: 'F. de registro',
      renderCell: ({ row }) => {
        return getFechaRegistro(row.fechaRegistro, row.idEstadoReclamo);
      }
    },
    {
      width: 170,
      field: 'paciente',
      headerName: 'Paciente'
    },
    {
      width: 150,
      field: 'financiador',
      headerName: 'Financiador'
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns, true), [delegaciones, tipoGestion]);

  // const sortModelMemo = useMemo(() => {
  //     if (!filters?.orderBy) return;
  //     // return [filters.orderBy]
  //     return [{
  //         field: Object.keys(filters.orderBy).at(),
  //         sort: Object.values(filters.orderBy).at().toLowerCase(),
  //     }]
  // }, [filters?.orderBy]);

  const [getDelegaciones, { loading: delegacionesLoading, data: delegacionesData }] = useLazyQuery(GET_DELEGACIONES, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!delegacionesData) return;

    setDelegaciones(adaptDelegacionReclamosArray(delegacionesData));
  }, [delegacionesData]);

  const [getDelegacionOrigen, { data: delegacionOrigenData }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!delegacionOrigenData) return;

    if (delegacionOrigenData.usuarioFull.success) {
      const deleg = parseInt(delegacionOrigenData.usuarioFull.valorEsperado.delegacionOrigen, 10);
      sessionStorage.setItem('delegacionOrigen', deleg);
      setDelegacionOrigen(deleg);

      if (filters.idDelegacion == 0)
        handleChangeFilters('idDelegacion', deleg);
    }
  }, [delegacionOrigenData]);

  useEffect(() => {
    if (rutasStorage) {
      const rutas = JSON.parse(rutasStorage);
      const rutaQueSirve = location.pathname;

      setAllowedWrite(rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
      )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura);

    }
  }, [rutasStorage]);

  useEffect(() => {
    const canEditResponsables = rolesByUserId?.includes(ROL_JEFE_CALIDAD_ID) || rolesByUserId?.includes(ROL_RESPONSABLE_CALIDAD_ASISTENCIAL_ID);
    setChildrenAction(() =>
      <div className='top-buttons'>
        {canEditResponsables && <EditarResponsablesModal
          areasReclamo={areasReclamo} />
        }
        <CustomButton
          className='button-header'
          label={`Nuevo ${isEFI ? 'EFI' : 'Reclamo'}`}
          onClick={handleClickCreate}
          variant='contained' />

      </div>
    );
  }, [isEFI, setChildrenAction, areasReclamo, rolesByUserId]);

  useEffect(() => {
    if (!delegacionesLoading) {
      getDelegaciones();
    }
    getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId) } });
  }, [storageId]);

  const handleExportar = async (idGestionReclamo) => {

    try {
      const informeReclamo = await getInformeReclamo({
        variables: {
          idGestionReclamo: idGestionReclamo
        }
      });

      if (!informeReclamo?.data?.informeReclamoById) {
        throw 'No se pudo descargar la información, intentelo nuevamente más tarde.';
        // return toast.error('No se pudo descargar la información, intentelo nuevamente más tarde.');
      }

      let htmlPdf = "";
      const informeHTML = document.createElement('div');
      const root = createRoot(informeHTML);

      flushSync(() => {
        root.render(<InformeReclamoTemplate reclamo={adaptInformeReclamo(informeReclamo?.data?.informeReclamoById)} />);
      });

      htmlPdf += informeHTML.innerHTML;

      await getPrintInforme({
        variables: {
          html: htmlPdf
        }
      });
    } catch (error) {
      console.log(error);
      // toast.error('No se pudo exportar el Resumen, intentelo nuevamente más tarde.')
    } finally {
    }
  };

  const handleClickCreate = () => {
    navigate(`/calidad/crear/${tipoGestion}`,
      {
        state: {
          idGestionReclamo: 0,
          id: 0,
          idEtapa: 2,
          nombreEtapa: 'Pendiente Creador',
          idEstado: 0,
          isNew: true,
          allowedWrite: true,
          estados: estadosReclamo
        }
      }
    );
  };

  const handleClickEdit = (row) => {
    navigate(`/calidad/crear/${tipoGestion}`,
      {
        state: {
          idGestionReclamo: row.idGestionReclamo,
          id: row.id,
          idEtapa: row.idEtapaReclamo,
          nombreEtapa: row.etapa,
          idEstado: row.idEstadoReclamo,
          reclamo: row,
          isNew: false,
          allowedWrite: true,
          estados: estadosReclamo
        }
      }
    );
  };

  const handleClickView = (row) => {
    navigate(`/calidad/crear/${tipoGestion}`,
      {
        state: {
          idGestionReclamo: row.idGestionReclamo,
          id: row.id,
          idEtapa: row.idEtapaReclamo,
          nombreEtapa: row.etapa,
          idEstado: row.idEstadoReclamo,
          reclamo: row,
          isNew: false,
          allowedWrite: false,
          estados: estadosReclamo
        }
      }
    );
  };

  const handleTextClean = () => {
    handleChangeFilters('txtFiltro', '');
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    handleChangeFilters(name, value);
  };

  // const handleChangeSortModel = (model) => {
  //     const newSort = model?.at()?.sort?.toLowerCase() === 'asc' ? 'DESC' : 'ASC';
  //     handleChangeFilters('orderBy', {
  //         [model?.at()?.field]: newSort
  //     });
  // }
  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <Grid
        container
        columns={24}
        spacing={2}
        sx={{ marginBottom: '25px' }}>

        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 3.5 }}>
          <TextField
            label='Buscar'
            name='txtFiltro'
            size='small'
            type='text'
            fullWidth
            variant='outlined'
            key={`${tipoGestion}-txtFiltro`}
            value={filters?.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            // onKeyUp={handleTextFilter}
            sx={{ backgroundColor: 'white' }}
            InputProps={{
              endAdornment:
                <InputAdornment position='end'>
                  <SearchIcon
                    sx={{ cursor: 'pointer' }} />

                  <ClearIcon
                    onClick={handleTextClean}
                    sx={{ cursor: 'pointer' }} />

                </InputAdornment>

            }} />

        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 3.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Delegación</InputLabel>
            <Select
              labelId='select-delegacion'
              options={delegaciones}
              label='Delegación'
              key={isEFI}
              onChange={handleChange}
              value={filters?.idDelegacion || undefined}
              name='idDelegacion'>

              {delegaciones && delegaciones.map((delegacion) => {
                return <MenuItem key={delegacion.id} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 3 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-area-responsable'>Área responsable</InputLabel>
            <Select
              labelId='select-area-responsable'
              options={[opcionTodos, ...areasReclamo]}
              label='Área responsable'
              onChange={handleChange}
              value={filters?.idArea || undefined}
              name='idArea'>

              {areasReclamo && [opcionTodos, ...areasReclamo].map((areasReclamo) => {
                return <MenuItem key={areasReclamo.id} value={areasReclamo.id}> {areasReclamo.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 2.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-estao'>Estado</InputLabel>
            <Select
              labelId='select-estao'
              options={estadosReclamo}
              label='Estado'
              onChange={handleChange}
              value={filters?.idEstado || undefined}
              name='idEstado'>

              {estadosReclamo && estadosReclamo.map((estado) => {
                return <MenuItem key={estado.id} value={estado.id}> {estado.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 2.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-clasificacion-ir'>Clasificación IR</InputLabel>
            <Select
              labelId='select-clasificacion-ir'
              options={clasificacionesIR}
              label='Clasificación IR'
              onChange={handleChange}
              value={filters?.idClasificacionReclamo || undefined}
              name='idClasificacionReclamo'>

              {clasificacionesIR && clasificacionesIR.map((clasifIR) => {
                return <MenuItem key={clasifIR.id} value={clasifIR.id}> {clasifIR.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 2.5 }}>
          <DateInput
            label="Fecha Desde"
            disabled={filters?.tipoVencimiento !== 1}
            name='fechaDesde'
            value={filters?.fechaDesde}
            onChange={(value) => handleChangeFilters('fechaDesde', moment(value).format('yyyy-MM-DD HH:mm:ss'))} />

        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 2.5 }}>
          <DateInput
            label="Fecha Hasta"
            disabled={filters?.tipoVencimiento !== 1}
            name='fechaHasta'
            value={filters?.fechaHasta}
            onChange={(value) => handleChangeFilters('fechaHasta', moment(value).set({ hour: 23, minute: 59, second: 59 }).format('yyyy-MM-DD HH:mm:ss'))} />

        </Grid>
        <Grid sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 24, sm: 12, md: 6, lg: 3, xl: 4 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-vencimiento'>Vencimiento</InputLabel>
            <Select
              labelId='select-vencimiento'
              options={vencimientoOptions}
              label='Clasificación IR'
              onChange={handleChange}
              value={filters?.tipoVencimiento || undefined}
              name='tipoVencimiento'>

              {vencimientoOptions && vencimientoOptions.map((tipoVencimiento) => {
                return <MenuItem key={tipoVencimiento.id} value={tipoVencimiento.id}> {tipoVencimiento.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <DataGridGenerico
        columns={columns}
        totalCount={totalCount}
        rows={reclamos}
        toolbar={false}
        loading={reclamosLoading}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        // onSortModelChange={handleChangeSortModel}
        // sortModel={sortModelMemo}
        maxWidth={columns.reduce((total, column) => total + (column.width || 0), 0)} />

    </div>);

};

const useGetReclamos = (tipoGestion, delegacionOrigen, isEFI) => {

  const [reclamos, setReclamos] = useState([]);
  const [paginationStatus, setPaginationStatus] = useState({
    pageSize: 10,
    page: 0
  });

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: delegacionOrigen,
    idEstado: -1,
    idArea: -1,
    idClasificacionReclamo: -1,
    fechaDesde: moment().add(-10, 'd').format("yyyy-MM-DD"),
    fechaHasta: moment().add(20, 'd').format("yyyy-MM-DD"),
    txtFiltro: '',
    tipoVencimiento: 1,
  }, `${tipoGestion}Filter`);

  const debouncedTextFilter = useDebounce(filters.txtFiltro, 1000);

  const filtroFechaVencidos = useMemo(() => {
    const now = moment();

    switch (filters.tipoVencimiento) {
      case 1: // Rango de fechas personalizado
        return {
          and: [
            { fechaRegistro: { gte: moment(filters.fechaDesde) } },
            { fechaRegistro: { lte: moment(filters.fechaHasta) } }]

        };

      case 2: // Reclamos entre 20 y 29 días
        return {
          and: [
            { fechaRegistro: { gte: now.clone().subtract(29, 'days') } },
            { fechaRegistro: { lte: now.clone().subtract(20, 'days') } }]

        };

      default: // Reclamos con más de 30 días
        return {
          fechaRegistro: {
            lte: now.clone().subtract(30, 'days')
          }
        };
    }
  }, [filters.tipoVencimiento, filters.fechaDesde, filters.fechaHasta]);

  const buildFilters = (filterValue) => {
    if (!filterValue) return null;

    // Divide el valor en palabras
    const terms = filterValue.trim().split(/\s+/);

    // Si solo hay un término, busca en todos los campos relevantes individualmente
    if (terms.length === 1) {
      return {
        or: [
          parseInt(terms[0]) > 0 ? { numeroReclamo: { eq: parseInt(terms[0]) } } : {},
          { idPacienteNavigation: { nombre: { contains: terms[0] } } },
          { idPacienteNavigation: { apellido: { contains: terms[0] } } },
          { idEtapaReclamoNavigation: { descripcion: { contains: terms[0] } } }]

      };
    }

    // Si hay más de un término, busca cada palabra en todos los campos relevantes sin agrupar
    return {
      and: terms.map((term) => ({
        or: [
          { idPacienteNavigation: { nombre: { contains: term } } },
          { idPacienteNavigation: { apellido: { contains: term } } },
          { idEtapaReclamoNavigation: { descripcion: { contains: term } } }]

      }))
    };
  };

  const [getReclamos, { reclamosLoading, data: reclamosData }] = useLazyQuery(GET_RECLAMOS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!reclamosData) return;
    setReclamos(reclamosData);
  }, [reclamosData]);

  useEffect(() => {
    if (reclamosLoading) return;
    getReclamos({
      variables: {
        skip: paginationStatus.page * paginationStatus.pageSize,
        take: paginationStatus.pageSize,
        where: {
          and: [
            { idTipoReclamo: { eq: isEFI ? tipoReclamo.EFI : tipoReclamo.RECLAMO } },
            debouncedTextFilter ? buildFilters(debouncedTextFilter) ?? {} : {},
            filters.idDelegacion > -1 ?
              { idDelegacion: { eq: filters.idDelegacion } } :
              {},
            filters.idEstado !== -1 //Si la opción es -1 es todos, se borra el filtro
              ? { idEstadoReclamo: { eq: filters.idEstado } } :
              {},
            filters.idArea !== -1 //Si la opción es -1 es todos, se borra el filtro
              ? { idAreaReclamo: { eq: filters.idArea } } :
              {},
            filters.idClasificacionReclamo !== -1 //Si la opción es -1 es todos, se borra el filtro
              ? { idClasificacionReclamo: { eq: filters.idClasificacionReclamo } } :
              {},
            filtroFechaVencidos]
        },
        order: [{ numeroReclamo: 'DESC' }]
      }
    });
  }, [filters, debouncedTextFilter, isEFI, paginationStatus]);

  const onPageChange = (page) => {
    setPaginationStatus((pagStatus) => ({ ...pagStatus, page: page }));
  };

  const onPageSizeChange = (pageSize) => {
    setPaginationStatus((pagStatus) => ({ ...pagStatus, pageSize: pageSize, page: 0 }));
  };

  return {
    reclamos: adaptReclamosArray(reclamos),
    reclamosLoading,
    totalCount: reclamos?.allReclamos?.totalCount,
    page: paginationStatus.page,
    pageSize: paginationStatus.pageSize,
    filters,
    handleChangeFilters,
    setReclamos,
    onPageChange,
    onPageSizeChange
  };
};